import { Alert, Button, Checkbox, Flex, Modal, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { webhookUrl } from "@/common/artemis-operations/webhooks";
import { confirmDestructive } from "@/common/confirm";
import { COPYABLE } from "@/components/base";

interface WebhookSecretDialogProps {
	/** The webhook whose secret was just issued; null closes the dialog. */
	issued: { webhookId: string; secret: string } | null;
	onClose: () => void;
}

/**
 * Shows a webhook's URL and secret right after create or rotate — the only time the secret is ever
 * available. How to call it lives in the public docs, not here. Like the agent credential modal,
 * it cannot be dismissed until the user confirms they stored the secret.
 */
export function WebhookSecretDialog({
	issued,
	onClose,
}: WebhookSecretDialogProps) {
	const [acknowledged, setAcknowledged] = useState(false);

	// A fresh secret always starts un-acknowledged, even if the dialog is reused.
	useEffect(() => {
		if (issued) setAcknowledged(false);
	}, [issued]);

	const closeOrConfirm = () => {
		if (acknowledged) {
			onClose();
			return;
		}
		confirmDestructive({
			title: "Close without storing the secret?",
			content:
				"It is not shown again. You would have to rotate it to get a new one.",
			okText: "Close anyway",
			onOk: onClose,
		});
	};

	if (!issued) return null;

	const url = webhookUrl(issued.webhookId);

	return (
		<Modal
			title="Webhook secret"
			open
			width={720}
			// Closing without the acknowledgement asks first: the secret is never shown again.
			closable
			maskClosable={false}
			onCancel={closeOrConfirm}
			centered
			// Long content scrolls inside the body; the acknowledgement and Done stay in view.
			styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
			footer={
				<Flex align="center" justify="space-between" gap={16}>
					<Checkbox
						checked={acknowledged}
						onChange={(e) => setAcknowledged(e.target.checked)}
					>
						I stored the secret in a safe place.
					</Checkbox>
					<Button type="primary" disabled={!acknowledged} onClick={onClose}>
						Done
					</Button>
				</Flex>
			}
			destroyOnHidden
		>
			<Space vertical size="middle" style={{ width: "100%" }}>
				<Alert
					type="warning"
					title="Copy the secret now"
					description="It is shown only once. Anyone holding it can invoke this webhook; rotate it from the webhook if it leaks."
				/>
				<Typography.Text strong>URL</Typography.Text>
				<Typography.Text code copyable={{ ...COPYABLE, text: url }}>
					{url}
				</Typography.Text>
				<Typography.Text strong>Secret</Typography.Text>
				<Typography.Text code copyable={{ ...COPYABLE, text: issued.secret }}>
					{issued.secret}
				</Typography.Text>
				<Typography.Paragraph>
					How to call it, with or without headers, is in the{" "}
					<a
						href="https://docs.stratonext.ai/webhooks/"
						target="_blank"
						rel="noreferrer"
					>
						webhooks documentation
					</a>
					.
				</Typography.Paragraph>
			</Space>
		</Modal>
	);
}
